/**
 * Globaler Filter-Store (Zustand).
 *
 * Wird von allen Charts gelesen. Klick auf einen Balken/Tabellenzeile setzt
 * einen Filter, alle Charts reagieren reaktiv.
 *
 * Filter werden außerdem in die URL serialisiert (pushState), damit Dashboards
 * teilbar/bookmarkbar sind.
 */

import { create } from 'zustand';
import type { FilterRequest } from '@statistik/shared-types';

type FilterValue = string[] | { min?: string; max?: string };

interface FilterStore {
  filters: Record<string, FilterValue>;
  addFilter: (dim: string, value: string | number) => void;
  removeFilter: (dim: string, value?: string | number) => void;
  setRangeFilter: (dim: string, min?: string, max?: string) => void;
  setAll: (filters: Record<string, FilterValue>) => void;
  clearAll: () => void;
  hasFilter: (dim: string, value?: string | number) => boolean;
  toRequest: () => FilterRequest;
}

export const useFilterStore = create<FilterStore>((set, get) => ({
  filters: {},
  addFilter: (dim, value) => {
    set((state) => {
      const current = state.filters[dim];
      const arr = Array.isArray(current) ? current : [];
      const sval = String(value);
      if (arr.includes(sval)) return state;
      return { filters: { ...state.filters, [dim]: [...arr, sval] } };
    });
    persistToUrl(get().filters);
  },
  removeFilter: (dim, value) => {
    set((state) => {
      if (value === undefined) {
        // ganzen Dim-Filter entfernen
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      const current = state.filters[dim];
      if (!Array.isArray(current)) return state;
      const filtered = current.filter((v) => v !== String(value));
      if (filtered.length === 0) {
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      return { filters: { ...state.filters, [dim]: filtered } };
    });
    persistToUrl(get().filters);
  },
  setRangeFilter: (dim, min, max) => {
    set((state) => {
      if (!min && !max) {
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      return { filters: { ...state.filters, [dim]: { min, max } } };
    });
    persistToUrl(get().filters);
  },
  setAll: (filters) => {
    set({ filters });
    persistToUrl(filters);
  },
  clearAll: () => {
    set({ filters: {} });
    persistToUrl({});
  },
  hasFilter: (dim, value) => {
    const current = get().filters[dim];
    if (!current) return false;
    if (value === undefined) return true;
    if (Array.isArray(current)) return current.includes(String(value));
    return false;
  },
  toRequest: () => ({ filters: get().filters }),
}));

function persistToUrl(filters: Record<string, FilterValue>) {
  try {
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    sp.delete('f');
    if (Object.keys(filters).length > 0) {
      sp.set('f', btoa(encodeURIComponent(JSON.stringify(filters))));
    }
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* ignore */
  }
}

/** Laden initialer Filter aus der URL (z.B. via Bookmark). */
export function loadFiltersFromUrl(): void {
  try {
    const sp = new URL(window.location.href).searchParams;
    const f = sp.get('f');
    if (!f) return;
    const parsed = JSON.parse(decodeURIComponent(atob(f))) as Record<string, FilterValue>;
    useFilterStore.setState({ filters: parsed });
  } catch {
    /* ignore */
  }
}
